import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Building2, Crown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * WorkspaceAccessDialog (Feb 2026)
 *
 * Per-user multi-tenant access toggle. Admin opens this from the Users
 * page → row action "Workspace access" → flips a switch for each
 * workspace the user could potentially access.
 *
 * The user's PRIMARY workspace is shown but disabled (can't be revoked
 * here). Other workspaces have a switch; flipping ON calls
 * /api/users/{user_id}/grant-tenant-access, flipping OFF calls
 * /api/users/{user_id}/revoke-tenant-access.
 */
export default function WorkspaceAccessDialog({ user, open, onOpenChange }) {
  const [tenants, setTenants] = useState([]);
  const [userAccess, setUserAccess] = useState(new Set());
  const [primaryTenantId, setPrimaryTenantId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(null); // tenant_id currently being toggled

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      // Get all active tenants
      const allRes = await api.get('/tenants/public');
      const allTenants = (allRes?.data || allRes)?.tenants || [];
      setTenants(allTenants);

      // Determine which tenants this user currently has access to.
      // tenant_users records with the same email indicate access.
      const userEmail = (user.email || '').toLowerCase();
      if (!userEmail) {
        setUserAccess(new Set([user.tenant_id]));
        setPrimaryTenantId(user.tenant_id);
        return;
      }
      const usersRes = await api.get('/tenants/users-by-email', { params: { email: userEmail } }).catch(() => null);
      // Fallback: use the user's own tenant_id as primary, no extras
      let accessIds = new Set();
      let primary = user.tenant_id || null;
      if (usersRes) {
        const data = usersRes.data || usersRes;
        accessIds = new Set((data.tenant_ids || []).filter(Boolean));
        primary = data.primary_tenant_id || primary;
      }
      if (primary) accessIds.add(primary);
      if (user.tenant_id) accessIds.add(user.tenant_id);
      setUserAccess(accessIds);
      setPrimaryTenantId(primary);
    } catch (e) {
      console.error('workspace access load failed', e);
      toast.error('Could not load workspace list');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleToggle = async (tenantId, currentlyHasAccess) => {
    if (tenantId === primaryTenantId) return; // can't revoke primary
    setToggling(tenantId);
    try {
      if (currentlyHasAccess) {
        await api.post(`/users/${user.id}/revoke-tenant-access`, { tenant_id: tenantId });
        setUserAccess(prev => {
          const s = new Set(prev); s.delete(tenantId); return s;
        });
        toast.success('Workspace access revoked');
      } else {
        await api.post(`/users/${user.id}/grant-tenant-access`, { tenant_id: tenantId });
        setUserAccess(prev => new Set(prev).add(tenantId));
        toast.success('Workspace access granted');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Toggle failed');
    } finally {
      setToggling(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="workspace-access-dialog"
        className="max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-orange-600" />
            Workspace access
          </DialogTitle>
          <DialogDescription>
            Choose which workspaces <strong>{user?.name || user?.email}</strong> can switch to. Toggling ON
            mirrors this user into the target workspace using the same email + login.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {loading && (
            <>
              {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            </>
          )}
          {!loading && tenants.map((t) => {
            const hasAccess = userAccess.has(t.id);
            const isPrimary = t.id === primaryTenantId;
            const isBusy = toggling === t.id;
            return (
              <div
                key={t.id}
                data-testid={`workspace-row-${t.slug}`}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  hasAccess ? 'border-orange-200 bg-orange-50/40 dark:bg-orange-950/10' : 'border-slate-200 dark:border-slate-800'
                }`}
              >
                <div
                  className="h-9 w-9 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0"
                  style={{ background: t.branding?.primary_color || '#6366f1' }}
                >
                  {(t.name || 'W').charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">{t.name}</p>
                    {t.region && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0">{t.region}</Badge>
                    )}
                    {isPrimary && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 bg-blue-50 text-blue-700 border-blue-200 flex items-center gap-0.5">
                        <Crown className="h-2.5 w-2.5" /> primary
                      </Badge>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500">
                    {isPrimary
                      ? "User's home workspace — cannot be revoked here"
                      : hasAccess
                        ? 'Active — user can switch to this workspace'
                        : 'Not granted — user cannot see this workspace'}
                  </p>
                </div>
                {isBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                ) : (
                  <Switch
                    data-testid={`workspace-toggle-${t.slug}`}
                    checked={hasAccess}
                    onCheckedChange={() => handleToggle(t.id, hasAccess)}
                    disabled={isPrimary}
                  />
                )}
              </div>
            );
          })}
          {!loading && tenants.length === 0 && (
            <p className="text-sm text-center text-slate-500 py-6">
              No workspaces available.
            </p>
          )}
        </div>

        <div className="flex justify-end pt-2 border-t border-slate-100 dark:border-slate-800">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
